import clsx from 'clsx';
import {
  type ButtonHTMLAttributes,
  type DetailedHTMLProps,
  type InputHTMLAttributes,
  type KeyboardEvent,
  type ReactNode,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';
import { BiSolidChevronDown } from 'react-icons/bi';
import { FiMinus, FiPlus } from 'react-icons/fi';

import s from './FormInputs.module.scss';

type NativeInputProps = DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>;
type NativeButtonProps = DetailedHTMLProps<
  ButtonHTMLAttributes<HTMLButtonElement>,
  HTMLButtonElement
>;

export const FormInput = ({
  label,
  error,
  mandatory,
  className,
  ...props
}: {
  label?: string;
  error?: string;
  mandatory?: boolean;
} & NativeInputProps) => (
  <label className={clsx(s.InputRoot, className)}>
    {label && (
      <div className={s.Label}>
        <span>{label}</span>
        {mandatory && <span>*</span>}
      </div>
    )}
    <div className={clsx(s.InputWrapper, error && s.error)}>
      <input type="text" {...props} />
    </div>
    {!!error && <p className={s.Error}>{error}</p>}
  </label>
);

type Option<T> = { label: ReactNode; value: T; disabled?: boolean };

export function FormSelect<T extends number | string>({
  label,
  error,
  placeholder,
  options,
  value,
  onChange,
  mandatory,
  disabled,
  className,
}: {
  label: string;
  error?: string;
  placeholder?: string;
  options: Option<T>[];
  value: T;
  onChange: (value: T) => void;
  mandatory?: boolean;
  disabled?: boolean;
  className?: string;
}) {
  const [opened, setOpened] = useState(false);
  const ref = useRef<HTMLLabelElement>(null);

  useEffect(() => {
    if (!opened) return;
    const onDocClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpened(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [opened]);

  const selectedLabel = options.find((o) => o.value === value)?.label ?? '';

  const select = (option: Option<T>) => {
    if (option.disabled) return;
    onChange(option.value);
    setOpened(false);
  };

  return (
    <label
      className={clsx(s.InputRoot, className)}
      ref={ref}
      onClick={() => !disabled && setOpened((v) => !v)}
    >
      <div className={s.Label}>
        <span>{label}</span>
        {mandatory && <span>*</span>}
      </div>
      <div
        className={clsx(
          s.InputWrapper,
          s.SelectWrapper,
          s.withIcon,
          disabled && s.disabled,
          error && s.error,
          opened && s.Opened,
        )}
      >
        <div className={s.Input}>
          {selectedLabel || <span className={s.Placeholder}>{placeholder}</span>}
        </div>
        <BiSolidChevronDown className={s.icon} />
        {opened && (
          <ul className={s.Popout}>
            {options.map((option) => (
              <li
                key={String(option.value)}
                className={option.disabled ? s.optionDisabled : undefined}
                onClick={(e) => {
                  e.stopPropagation();
                  select(option);
                }}
              >
                {option.label}
              </li>
            ))}
          </ul>
        )}
      </div>
      {!!error && <p className={s.Error}>{error}</p>}
    </label>
  );
}

export const FormButton = ({ children, className, ...props }: NativeButtonProps) => (
  <button type="button" className={clsx(s.Button, className)} {...props}>
    {children}
  </button>
);

export const FormButtonOutlined = ({ className, ...props }: NativeButtonProps) => (
  <FormButton className={clsx(s.OutlinedButton, className)} {...props} />
);

export const FormButtonOutlinedSmall = ({ className, ...props }: NativeButtonProps) => (
  <FormButtonOutlined className={clsx(s.SmallButton, className)} {...props} />
);

export const FormButtonOutlinedError = ({ className, ...props }: NativeButtonProps) => (
  <FormButtonOutlined className={clsx(s.OutlinedErrorButton, className)} {...props} />
);

export const Radio = ({
  checked,
  onChange,
  label,
  className,
}: {
  checked: boolean;
  onChange: () => void;
  label: string;
  className?: string;
}) => (
  <label className={clsx(s.Radio, className)}>
    <div className={clsx(s.circle, checked && s.checked)} />
    <div>{label}</div>
    <input className={s.input} type="radio" checked={checked} onChange={onChange} />
  </label>
);

/**
 * The count a typed string stands for, or null when it must not reach state. Digits only:
 * `Number()` alone would take "1e3", "0x10" and "" (as 0), and `parseInt` would read "2.5"
 * as 2 — each would save a number nobody typed.
 */
const parseCount = (text: string, min: number, max: number): number | null => {
  const trimmed = text.trim();
  if (!/^\d+$/.test(trimmed)) return null;
  const count = Number(trimmed);
  return Number.isSafeInteger(count) && count >= min && count <= max ? count : null;
};

/**
 * Integer picker: minus and plus on the sides, the value in the middle. Preferred over
 * a select when the value is an open-ended count rather than a fixed set of choices.
 *
 * The middle is a field too, for reaching 40 without forty clicks. What is typed is a
 * draft: it reaches `onChange` only as a whole number within `min`…`max`, so letters,
 * decimals, a sign or an empty box never land in the caller's state, and whatever the
 * caller derives from `value` (a price) moves exactly as it does for the buttons. An
 * invalid draft is marked and explained, and leaving the field puts the value back.
 */
export const NumberStepper = ({
  label,
  value,
  onChange,
  min = 0,
  max,
  mandatory,
  disabled,
  className,
}: {
  label?: string;
  value: number;
  onChange: (value: number) => void;
  min?: number;
  /** Omitted means unbounded — nothing is silently capped. */
  max?: number;
  mandatory?: boolean;
  disabled?: boolean;
  className?: string;
}) => {
  const upper = max ?? Number.MAX_SAFE_INTEGER;
  const [draft, setDraft] = useState(String(value));
  const [shownValue, setShownValue] = useState(value);
  const errorId = useId();

  // The value moved from outside (a button, the form resetting): the field follows. Done
  // while rendering rather than in an effect, so the old number never flashes.
  if (value !== shownValue) {
    setShownValue(value);
    setDraft(String(value));
  }

  const invalid = parseCount(draft, min, upper) === null;
  const noun = label?.toLowerCase();

  const step = (delta: number) => {
    const next = Math.min(Math.max(value + delta, min), upper);
    setDraft(String(next));
    if (next !== value) onChange(next);
  };

  const type = (text: string) => {
    setDraft(text);
    const count = parseCount(text, min, upper);
    if (count !== null && count !== value) onChange(count);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;
    event.preventDefault();
    step(event.key === 'ArrowUp' ? 1 : -1);
  };

  const range = max === undefined ? `${min} or more` : `${min} to ${max}`;

  return (
    <div className={clsx(s.InputRoot, className)}>
      {label && (
        <div className={s.Label}>
          <span>{label}</span>
          {mandatory && <span>*</span>}
        </div>
      )}
      <div className={clsx(s.Stepper, invalid && s.error, disabled && s.disabled)}>
        <button
          type="button"
          onClick={() => step(-1)}
          disabled={disabled || value <= min}
          aria-label={noun ? `Decrease ${noun}` : 'Decrease'}
        >
          <FiMinus />
        </button>
        <input
          className={s.StepperValue}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          autoComplete="off"
          role="spinbutton"
          aria-label={label}
          aria-valuenow={value}
          aria-valuemin={min}
          aria-valuemax={max}
          aria-invalid={invalid || undefined}
          aria-describedby={invalid ? errorId : undefined}
          value={draft}
          disabled={disabled}
          onChange={(event) => type(event.target.value)}
          // Drops an invalid draft and tidies a valid one ("007" → "7").
          onBlur={() => setDraft(String(value))}
          onFocus={(event) => event.target.select()}
          onKeyDown={onKeyDown}
        />
        <button
          type="button"
          onClick={() => step(1)}
          disabled={disabled || value >= upper}
          aria-label={noun ? `Increase ${noun}` : 'Increase'}
        >
          <FiPlus />
        </button>
      </div>
      {invalid && (
        <p id={errorId} className={s.Error} role="alert">
          {`Please enter a whole number, ${range}.`}
        </p>
      )}
    </div>
  );
};

export const FormGroup = ({
  label,
  cols,
  children,
}: {
  label: string;
  cols: number;
  children: ReactNode;
}) => (
  <div className={s.FormGroup}>
    <p className={s.Label}>{label}</p>
    <div className={s.body} style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}>
      {children}
    </div>
  </div>
);
